// OneNote-zoeken via Microsoft Graph met een delegated token (Notes.Read.All).
// App-only is door Microsoft afgezet (maart 2025), daarom via de ingelogde gebruiker.
// Doorzoekt het gedeelde Team-notebook op de SharePoint-site FullFrameTeam942.
import { acquireGraphToken } from "./graphToken";
import { htmlToPlainText } from "./htmlText";

// Site "TM01. Operationeel" (FullFrameTeam942) — bevat het gedeelde notulen-notebook.
const ONENOTE_SITE_ID =
  "fullframe.sharepoint.com,b33b3f95-d2fc-4143-ad32-e9c9c7d08d60,471255f6-68ca-4b1b-a292-9ff9f7ff1f58";
const GRAPH = "https://graph.microsoft.com/v1.0";
const _sectieCache = { data: null, tijd: 0 };

/**
 * Zoek OneNote-pagina's op een trefwoord (bijv. klantnaam).
 * Returnt [{ id, title, webUrl, modified }]. Gooit een fout bij auth/permissie-problemen.
 */
export async function searchOnenote(query, { top = 15 } = {}) {
  const q = (query || "").trim();
  if (!q) return [];
  const token = await acquireGraphToken(["Notes.Read.All"]);
  const site = `${GRAPH}/sites/${encodeURIComponent(ONENOTE_SITE_ID)}/onenote`;
  const headers = { Authorization: `Bearer ${token}` };
  const fields = "$select=id,title,links,lastModifiedDateTime";

  // 1) Snelle notebook-brede full-text search. Let op: Graph's `?search=` is
  //    officieel alleen voor consumer-OneNote; op M365-notebooks komt hier
  //    geregeld een lege 200 terug terwijl er wél notulen bestaan ("pon bike",
  //    20 aug). Daarom is een lege uitkomst hier GEEN eindantwoord — dan alsnog
  //    per sectie zoeken.
  const fast = await fetch(`${site}/pages?search=${encodeURIComponent(q)}&${fields}&$top=${top}`, { headers });
  if (fast.ok) {
    const hits = ((await fast.json()).value || []).map(mapPage);
    if (hits.length) return hits;
  }

  // 2) Per sectie (ook Microsoft's aanbevolen route bij fout 20266 "maximum
  //    sections exceeded"). Titel-match per wóórd: "pon bike" moet ook
  //    "PON Bike – kennismaking" én "Pon: intake" kunnen vinden.
  // Secties wisselen zelden — 10 min cachen scheelt een call per zoekopdracht,
  // en recentste-eerst zorgt dat de kans op vroege treffers het grootst is.
  let sections = _sectieCache.data;
  if (!sections || Date.now() - _sectieCache.tijd > 10 * 60 * 1000) {
    const secRes = await fetch(`${site}/sections?$select=id,displayName,lastModifiedDateTime&$top=100`, { headers });
    if (!secRes.ok) {
      const t = await secRes.text().catch(() => "");
      throw new Error(`OneNote-zoeken mislukt (${secRes.status}): ${t.slice(0, 200)}`);
    }
    sections = ((await secRes.json()).value || [])
      .sort((a, b) => String(b.lastModifiedDateTime || "").localeCompare(String(a.lastModifiedDateTime || "")));
    _sectieCache.data = sections;
    _sectieCache.tijd = Date.now();
  }
  const woorden = q.toLowerCase().split(/\s+/).filter((w) => w.length >= 2);
  const titelMatch = (p) => {
    const t = (p.title || "").toLowerCase();
    if (!woorden.length) return false;
    // Alle woorden in de titel is een sterke match; anders telt het langste
    // woord (>=4) alleen ook — "pon bike" vindt dan ook een pagina "PON".
    if (woorden.every((w) => t.includes(w))) return true;
    return woorden.some((w) => w.length >= 4 && t.includes(w));
  };
  const capped = sections.slice(0, 60); // veiligheidscap tegen extreem grote notebooks

  // Max 6 secties tegelijk — 60 parallelle Graph-calls is vragen om 429's,
  // en een gethrottelde sectie zag er eerder uit als "geen resultaat".
  let throttled = false;
  let mislukt = 0;
  const perSection = [];
  const wachtrij = [...capped];
  const genoeg = () => perSection.reduce((n, v) => n + v.length, 0) >= top;
  const werker = async () => {
    for (;;) {
      const s = wachtrij.shift();
      if (!s || throttled || genoeg()) return; // genoeg treffers → stoppen
      try {
        // Per sectie eerst full-text search; bij een fout óf nul treffers
        // titel-matching over recente pagina's.
        let r = await fetch(`${site}/sections/${s.id}/pages?search=${encodeURIComponent(q)}&${fields}&$top=${top}`, { headers });
        if (r.status === 429) { throttled = true; return; }
        if (r.ok) {
          const v = (await r.json()).value || [];
          if (v.length) { perSection.push(v); continue; }
        }
        r = await fetch(`${site}/sections/${s.id}/pages?${fields}&$top=100&$orderby=lastModifiedDateTime desc`, { headers });
        if (r.status === 429) { throttled = true; return; }
        if (!r.ok) { mislukt += 1; continue; }
        perSection.push(((await r.json()).value || []).filter(titelMatch));
      } catch { mislukt += 1; }
    }
  };
  await Promise.all(Array.from({ length: 6 }, werker));

  // Dedupe op id, sorteer op recent, cap op `top`.
  const seen = new Set();
  const hits = perSection.flat()
    .filter((p) => p && p.id && !seen.has(p.id) && seen.add(p.id))
    .sort((a, b) => new Date(b.lastModifiedDateTime || 0) - new Date(a.lastModifiedDateTime || 0))
    .slice(0, top)
    .map(mapPage);
  // Niets gevonden terwijl Graph ons afknelde of secties faalden? Zeg dat
  // eerlijk — een stil leeg resultaat leest als "die notulen bestaan niet".
  if (!hits.length && (throttled || mislukt > 0)) {
    throw new Error(throttled
      ? "OneNote remt het zoektempo af (429) — probeer het zo nog eens"
      : `zoeken faalde in ${mislukt} secties — probeer het zo nog eens`);
  }
  return hits;
}

function mapPage(p) {
  return {
    id: p.id,
    title: p.title || "(zonder titel)",
    webUrl: p.links?.oneNoteWebUrl?.href || null,
    modified: p.lastModifiedDateTime || null,
  };
}

/** Metadata van één OneNote-pagina zoals hij NU in OneNote staat: { id, title, webUrl, modified }. */
export async function getOnenotePage(pageId) {
  const token = await acquireGraphToken(["Notes.Read.All"]);
  const res = await fetch(
    `${GRAPH}/sites/${encodeURIComponent(ONENOTE_SITE_ID)}/onenote/pages/${pageId}?$select=id,title,links,lastModifiedDateTime`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw new Error(`Pagina ophalen mislukt (${res.status})`);
  return mapPage(await res.json());
}

/** Platte tekst van één OneNote-pagina (voor context in de chat). */
export async function getOnenotePageText(pageId) {
  const token = await acquireGraphToken(["Notes.Read.All"]);
  const res = await fetch(
    `${GRAPH}/sites/${encodeURIComponent(ONENOTE_SITE_ID)}/onenote/pages/${pageId}/content`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  if (!res.ok) throw new Error(`Pagina ophalen mislukt (${res.status})`);
  const html = await res.text();
  // HTML → platte tekst mét regeleinden (kaal textContent plakte alinea's aan elkaar).
  return htmlToPlainText(html);
}
